import { ChangeDetectionStrategy, Component, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatDialogModule, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { ApplicationApiService, RoadmapCommentDto, UserProfile } from 'src/app/services/application-api.service';
import { MatTooltipModule } from '@angular/material/tooltip';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { faTrashAlt, faWindowClose, faReply } from '@fortawesome/free-solid-svg-icons';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { SnackBarService, UserProfileService } from 'services';

export interface RoadmapCommentsDialogData {
  roadmapId: number;
  targetConditionId: number;
  roadmapTargetId: number;
  roadmapVersion: number;
  targetConditionName: string;
  comments: RoadmapCommentDto[];
  appUser?: UserProfile;
  userIsApproverForRegion: boolean;
  userIsApproverForProfitCenter: boolean;
}

@Component({
  selector: 'app-roadmap-comments',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatButtonModule,
    MatIconModule,
    MatDialogModule,
    MatFormFieldModule,
    MatInputModule,
    FontAwesomeModule,
    MatTooltipModule,
    MatInputModule,
    MatProgressBarModule
  ],
  templateUrl: './roadmap-comments.component.html',
  styleUrl: './roadmap-comments.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class RoadmapCommentsComponent {
  private readonly dialogRef = inject(MatDialogRef<RoadmapCommentsComponent>);
  private readonly apiService = inject(ApplicationApiService);
  private snackbar = inject(SnackBarService);
  protected readonly data: RoadmapCommentsDialogData = inject(MAT_DIALOG_DATA);
  protected readonly isLoading = signal(false);
  protected readonly comments = signal<RoadmapCommentDto[]>(this.data.comments);
  protected readonly faTrashAlt = faTrashAlt;
  protected readonly faWindowClose = faWindowClose;
  protected readonly faReply = faReply;
  protected newCommentText = '';

  deleteComment(id: number): void {
    this.isLoading.set(true);
    this.apiService.apiCommentsDelete(id).subscribe({
      next: () => {
        const index = this.data.comments.findIndex(c => c.id === id);
        if (index > -1) {
          this.data.comments.splice(index, 1);
        }
        this.comments.set([...this.data.comments]);
        this.snackbar.success('Comment deleted successfully');
        this.isLoading.set(false);
      },
      error: () => {
        this.snackbar.error('Failed to delete comment');
        this.isLoading.set(false);
      }
    });
  }

  save(): void {
    if (!this.newCommentText || this.newCommentText.trim() === '') {
      this.snackbar.error('Comment text cannot be empty');
      return;
    }
    this.isLoading.set(true);
    const newComment: RoadmapCommentDto = {
      id: 0,
      roadmapId: this.data.roadmapId,
      targetConditionId: this.data.targetConditionId,
      roadmapTargetId: this.data.roadmapTargetId,
      commentText: this.newCommentText.trim(),
      createdBy: this.data.appUser?.lastName + ', ' + this.data.appUser?.firstName,
      createdOn: new Date(),
      roadmapVersion: this.data.roadmapVersion,
      type: (this.data.appUser?.isAdminUser) ? 'admin' : (this.data.userIsApproverForRegion) ? 'ra' : (this.data.userIsApproverForProfitCenter) ? 'pca' : 'pt',
    };
    this.apiService.apiCommentsPost(newComment).subscribe({
      next: (createdComment: RoadmapCommentDto) => {
        let comments = this.comments();
        comments.push(createdComment);
        this.comments.set(comments);
        this.newCommentText = '';
        this.snackbar.success('Comment saved successfully');
        this.isLoading.set(false);
        this.dialogRef.close();
      },
      error: () => {
        this.snackbar.error('Failed to save comment');
        this.isLoading.set(false);
      }
    });
  }

  close(): void {
    this.dialogRef.close();
  }
}
